<template>
  <transition name="signing-modal-fade">
    <div v-show="visible" class="signing-modal-overlay" @click.self="close">
      <div class="signing-modal" @click.stop>
        <button class="signing-modal__close" @click="close">
          <i class="el-icon-close"></i>
        </button>

        <div class="signing-modal__icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              stroke="#534ab7"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <h3 class="signing-modal__title">Firmar y enviar al SRI</h3>
        <p class="signing-modal__subtitle">
          Esta acción es irreversible. El documento será validado y autorizado
          por el SRI electrónicamente.
        </p>

        <div class="signing-modal__doc">
          <div class="signing-modal__doc-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                stroke="#1d9e75"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="signing-modal__doc-info">
            <span class="signing-modal__doc-number">{{ documentNumber }}</span>
            <span class="signing-modal__doc-client">{{ clientName }}</span>
          </div>
          <div class="signing-modal__doc-amount">
            {{ formattedAmount }}
          </div>
        </div>

        <div class="signing-modal__actions">
          <button
            class="signing-modal__btn signing-modal__btn--cancel"
            @click="close"
          >
            Cancelar
          </button>
          <button
            class="signing-modal__btn signing-modal__btn--confirm"
            @click="confirm"
          >
            Confirmar firma
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "SigningConfirmModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    documentNumber: {
      type: String,
      default: ""
    },
    clientName: {
      type: String,
      default: ""
    },
    amount: {
      type: [Number, String],
      default: 0
    }
  },
  computed: {
    formattedAmount() {
      const num = parseFloat(this.amount) || 0;
      return "$" + num.toFixed(2);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        document.addEventListener("keydown", this.handleEsc);
      } else {
        document.removeEventListener("keydown", this.handleEsc);
      }
    }
  },
  mounted() {
    // Append to body to escape card/dropdown DOM hierarchy
    // This prevents el-dropdown mouseout events from causing flicker
    document.body.appendChild(this.$el);
  },
  methods: {
    handleEsc(e) {
      if (e.key === "Escape") this.close();
    },
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    confirm() {
      this.$emit("confirm");
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEsc);
    // Clean up: remove from body when component is destroyed
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  }
};
</script>

<style lang="scss">
.signing-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(45, 43, 85, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.signing-modal {
  background: #fff;
  border-radius: 14px;
  padding: 26px 24px 22px;
  width: 340px;
  max-width: 90vw;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);

  &__close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f0f2f7;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    i {
      font-size: 16px;
      color: #31355c;
      font-weight: 700;
    }

    &:hover {
      background: #e5e3db;
    }
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f1efe8;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
  }

  &__title {
    font-size: 15px;
    font-weight: 500;
    color: #2d2b55;
    text-align: center;
    margin: 0 0 6px;
  }

  &__subtitle {
    font-size: 12px;
    color: #888;
    text-align: center;
    margin: 0;
    line-height: 1.5;
  }

  &__doc {
    background: #f7f8fc;
    border: 0.5px solid #e8e7e3;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 13px 0 18px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__doc-icon {
    width: 30px;
    height: 30px;
    min-width: 30px;
    background: #e1f5ee;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__doc-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__doc-number {
    font-size: 11px;
    color: #888;
    line-height: 1.3;
  }

  &__doc-client {
    font-size: 13px;
    font-weight: 500;
    color: #2d2b55;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__doc-amount {
    font-size: 13px;
    font-weight: 500;
    color: #1d9e75;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  &__btn {
    flex: 1;
    padding: 9px;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.15s;
    font-family: inherit;

    &:hover {
      opacity: 0.85;
    }

    &--cancel {
      background: #fff;
      border: 0.5px solid #e8e7e3;
      color: #888;
      font-weight: 400;
    }

    &--confirm {
      background: #2d2b55;
      border: none;
      color: #fff;
      font-weight: 500;
    }
  }
}

.signing-modal-fade-enter-active,
.signing-modal-fade-leave-active {
  transition: opacity 0.2s;
}

.signing-modal-fade-enter,
.signing-modal-fade-leave-to {
  opacity: 0;
}

.signing-modal-fade-enter-active .signing-modal,
.signing-modal-fade-leave-active .signing-modal {
  transition: transform 0.2s;
}

.signing-modal-fade-enter .signing-modal {
  transform: scale(0.95);
}

.signing-modal-fade-leave-to .signing-modal {
  transform: scale(0.95);
}
</style>
